import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { get, debounce } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { EdmundsCpaNoAdhesionDecorator } from 'site-modules/shared/pages/decorators/edmunds-cpa-no-adhesion';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/dealerships/dealer-review';
import { SITE_URL } from 'site-modules/shared/pages/seo';
import { DealerInfoEntities, DealerInfoModel, getDealerPath } from 'client/data/models/dealer-info';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { DEALER_PROPS, REVIEW_TITLE, DEALER_COMMENT_PAGES } from 'client/constants/dealer-reviews';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { DealerReviewDetails } from 'site-modules/dealerships/components/dealer-review-details/dealer-review-details';
import { HTTP_NOT_FOUND } from 'client/utils/http-status';
import {
  formatReviewType,
  getReview,
  ReviewsModel,
  ReviewsPaths,
  DealerReviewEntity,
} from 'client/data/models/dealer-reviews';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { getModelState } from 'client/data/luckdragon/model';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { getDealershipStateCityNameUrl } from 'site-modules/shared/utils/dealership-link-constructor';
import { DealerReviewCommentForm } from 'client/site-modules/dealerships/components/dealer-review-comment-form/dealer-review-comment-form';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { UserDealerReview } from 'site-modules/dealerships/components/user-dealer-review/user-dealer-review';
import { UserComments } from 'client/site-modules/dealerships/components/user-comments/user-comments';
import { getDealerReviewPageBreadcrumbs } from 'site-modules/dealerships/utils/breadcrumbs';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';

import './dealer-review-comment.scss';

export class DealerReviewCommentUI extends Component {
  static propTypes = {
    params: DEALER_PROPS.isRequired,
    setModelValue: PropTypes.func.isRequired,
    dealerInfo: DealerInfoEntities.DealerInfo,
    dealerReview: DealerReviewEntity,
  };

  static defaultProps = {
    dealerInfo: {},
    dealerReview: null,
  };

  state = {
    comments: null,
  };

  componentDidMount() {
    TrackingHandler.useMap(TrackingMap);
  }

  shouldComponentUpdate({ dealerReview }) {
    return !!dealerReview;
  }

  onSuccessfulSubmit = debounce(() => {
    const {
      setModelValue,
      dealerReview: { id },
    } = this.props;

    setModelValue(ReviewsPaths.getDealershipReviewPath({ reviewId: id }), ReviewsModel, undefined);
  }, 500);

  render() {
    const { dealerInfo, dealerReview, params } = this.props;
    const { comments: updatedComments } = this.state;

    if (!get(dealerInfo, 'id') || !get(dealerReview, 'id')) return null;

    const { comments: initialComments, reviewType: type, title, id } = dealerReview;
    const comments = updatedComments || initialComments;
    const reviewType = formatReviewType(type);
    const reviewTypeTitle = REVIEW_TITLE[reviewType];
    const { address, rooftopLogicalName } = dealerInfo;
    const dealershipLink = getDealershipStateCityNameUrl({
      state: address.stateName,
      city: address.city,
      dealership: rooftopLogicalName,
      params: { type: reviewType },
    });
    const rightRail = (
      <Col xs={12} lg={4} className="mt-2">
        <div className="mb-3_5">
          <MrectNative position={'1'} refreshable={false} dfpCollapse />
        </div>
        <div className="pt-md-3_5 mb-2">
          <MrectNative
            position={'2'}
            refreshable={false}
            wiredOnly
            addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
            dfpCollapse
          />
        </div>
      </Col>
    );
    const breadcrumbs = getDealerReviewPageBreadcrumbs({ dealerInfo, reviewType });
    const { dealerState, dealerCity, logicalName, reviewId } = params;
    const updateLink = `/dealerships/${dealerState}/${dealerCity}/${logicalName}/update/${
      DEALER_COMMENT_PAGES[reviewType]
    }-${reviewId}.html`;

    return (
      <Container
        tag="main"
        id="main-content"
        tabIndex={-1}
        className="dealer-review-comment"
        data-tracking-parent={`dealer_${reviewType}_review`}
      >
        <Breadcrumbs className="ps-0" data={breadcrumbs} />
        <Fragment>
          <h1 className="heading-2 my-1_5">{dealerInfo.name}</h1>
          <DealerReviewDetails dealerInfo={dealerInfo} reviewType={reviewType} params={params} />
          <Row>
            <Col xs={12} lg={8} className="mt-2">
              <a href={dealershipLink} className="link">
                <span className="text-primary-darker">
                  <span className="icon-arrow-left3 me-0_5 small" aria-hidden />
                  Back to {reviewTypeTitle} Reviews
                </span>
              </a>
              <h2 className="heading-3 mb-0_5 mt-1">Dealership {reviewTypeTitle} Review</h2>
              <UserDealerReview review={getReview(dealerReview)} updateLink={updateLink} />
              <hr className="mb-1_5" />
              <DealerReviewCommentForm onSuccessfulSubmit={this.onSuccessfulSubmit} reviewId={id} />
              {!!comments.length && <UserComments comments={comments} reviewTitle={title} reviewId={id} />}
            </Col>
            {rightRail}
          </Row>
        </Fragment>
      </Container>
    );
  }
}

export const stateToPropsConfig = {
  dealerInfo: bindToPath(props => getDealerPath(props), DealerInfoModel),
  dealerReview: bindToPath(
    ({ params: { reviewId } }) => ReviewsPaths.getDealershipReviewPath({ reviewId }),
    ReviewsModel
  ),
};

export const DealerReviewComment = withRouter(connectToModel(DealerReviewCommentUI, stateToPropsConfig));

export default params =>
  pageDefinition({
    name: `car_inventory_ddp_sales_reviews_full`,
    category: 'car_inventory',
    preload: (store, props) => {
      const { logicalName, reviewId } = params;
      const { mobile } = store.getState();
      const preloader = new ModelPreloader(store);
      const dealerPath = getDealerPath({ ...props, params });

      preloader.resolve(dealerPath, DealerInfoModel);

      preloader.resolve(ReviewsPaths.getDealershipReviewPath({ reviewId }), ReviewsModel);

      return preloader.load().then(modelState => {
        const dealer = modelState.get(dealerPath, DealerInfoModel);
        const dealerReview = modelState.get(ReviewsPaths.getDealershipReviewPath({ reviewId }), ReviewsModel);

        if (!dealer) {
          const ex = new Error(`ValidatorError - Could not find a dealer - ${logicalName}`);
          ex.status = HTTP_NOT_FOUND;
          ex.redirectPageTo404 = true;
          throw ex;
        }

        if (!dealerReview) {
          const ex = new Error(`ValidatorError - Could not find a review - ${reviewId}`);
          ex.status = HTTP_NOT_FOUND;
          ex.redirectPageTo404 = true;
          throw ex;
        }

        preloader.resolve(ReviewsPaths.getOverallRatings({ dealerId: dealer.rooftopId }), ReviewsModel);
        const reviewType = formatReviewType(dealerReview.reviewType);

        store.dispatch(
          legacyPageUpdate({
            pageName: `car_inventory_ddp_${reviewType}_reviews_full`,
            categoryName: 'car_inventory',
          })
        );

        if (mobile) {
          preloader.resolve(`carcodeConfig.${dealer.rooftopId}`, DealerInfoModel);
        }

        return preloader.load();
      });
    },
    seo: (state, props) => {
      const canonical = `${SITE_URL}${props.location.pathname}`;
      const modelState = getModelState(state);
      const dealerInfo = modelState.get(getDealerPath({ ...props, params }), DealerInfoModel);

      if (!dealerInfo) {
        return {};
      }

      const { name: dealerName } = dealerInfo;

      return {
        title: `Dealer Review Comment for ${dealerName} | Edmunds`,
        description: `Comment on a Review for ${dealerName} on Edmunds.com`,
        canonical,
        robots: {
          follow: true,
          noIndex: true,
        },
      };
    },
    component: props => <DealerReviewComment {...props} params={params} />,
    decorator: EdmundsCpaNoAdhesionDecorator,
  });
